// src/App.js - Refactored
import React, { useMemo, useEffect } from 'react';
import { useKegOpsState } from './useKegOpsState';
import { useTheme } from './useTheme';
import { DashboardView } from './kegops-dashboard';
import { KegLandingView } from './kegops-landing';
import { AccountView, SettingsView } from './kegops-settings';
import { calculateAverageRating, getTotalRatings, renderStars } from './kegops-utils';
import * as SupabaseService from './supabaseService';

// Import filter utilities
import { 
  normalizeString, 
  createLocationCombinations, 
  filterBeersOnTap, 
  filterBeersOnDeck,
  filterKegs 
} from './utils';

// Import handlers
import { createKegHandlers } from './handlers/kegHandlers';
import { createBeerHandlers } from './handlers/beerHandlers';
import { createLocationHandlers } from './handlers/locationHandlers';
import { createAccountHandlers } from './handlers/accountHandlers';
import { createMaintenanceHandlers } from './handlers/maintenanceHandlers';

// Import modals
import { KegModal, QRModal, MaintenanceModal, TapNumberModal } from './modals/kegModals';
import { TapSelectionModal } from './modals/tapSelectionModal';
import { FillKegModal, RatingModal, FeedbackModal, CleanModal, BeerHistoryModal } from './modals/beerModals';
import { UserModal, LocationModal, SublocationModal } from './modals/settingsModals';
import { OnboardingModal } from './modals/OnboardingModal';
import { TapListView } from './tap-list';
import Login from './auth/Login';
// import AppHeader from './components/header/AppHeader';

export default function KegOpsApp({ initialKegId = null, initialTapLocation = null, initialTapSublocation = null }) {
  const [tapLocation] = React.useState(initialTapLocation);
  const [tapSublocation] = React.useState(initialTapSublocation);
  // Get all state from custom hook
  const state = useKegOpsState();
  
  const {
    currentView, setCurrentView,
    selectedKegId, setSelectedKegId,
    dashboardTab, setDashboardTab,
    isLoading, error,
    isAuthChecked,
    settings,
    account,
    kegs, setKegs,
    locations,
    breweries, setBreweries, selectedBreweryId, setSelectedBreweryId,
    searchTerm, setSearchTerm,
    filterTapStatus, setFilterTapStatus,
    filterCleanStatus, setFilterCleanStatus,
    filterLocation, setFilterLocation,
    showTapDropdown, setShowTapDropdown,
    showCleanDropdown, setShowCleanDropdown,
    expandedLocations, setExpandedLocations,
    quickFilter, setQuickFilter,
    showKegModal, setShowKegModal,
    showQRModal, setShowQRModal,
    showMaintenanceModal, setShowMaintenanceModal,
    showRatingModal, setShowRatingModal,
    showFeedbackModal, setShowFeedbackModal,
    showCleanModal, setShowCleanModal,
    showFillModal, setShowFillModal,
    showHistoryModal, setShowHistoryModal,
    showTapNumberModal, setShowTapNumberModal,
    tapModalKegId, /* setTapModalKegId, */
    // pendingTapStatus, setPendingTapStatus,
    showUserModal, setShowUserModal,
    showLocationModal, setShowLocationModal,
    showSublocationModal, setShowSublocationModal,
    editingKeg, setEditingKeg,
    selectedMaintenanceKeg, setSelectedMaintenanceKeg,
    selectedRatingKeg, setSelectedRatingKeg,
    selectedHistoryBeer, setSelectedHistoryBeer,
    kegForm, setKegForm,
    userForm, setUserForm,
    maintenanceForm, setMaintenanceForm,
    qrSettings, setQrSettings,
    ratingForm, setRatingForm,
    newLocation, setNewLocation,
    newSublocation, setNewSublocation,
    editingLocation, setEditingLocation,
    editingSublocation, setEditingSublocation,
    theme, setTheme,
    loadAllData,
    showOnboarding,
    isCreatingFirstBrewery,
    completeOnboarding
  } = state;

  // Initialize theme
  useTheme(theme, setTheme);

  // ============================================================
  // ROLE-BASED GATING HELPER
  // ============================================================
  /**
   * Check if current user has required role in selected brewery
   * @param {Array<string>} requiredRoles - Roles that are allowed
   * @returns {boolean}
   */
  const hasRole = (requiredRoles = []) => {
    if (!selectedBreweryId || !breweries) return false;
    const brewery = breweries.find(b => b.id === selectedBreweryId);
    return brewery && requiredRoles.includes(brewery.userRole);
  };

  // const canViewKegs = () => hasRole(['bartender', 'bar_manager', 'bar_owner']);
  const canManageSettings = () => hasRole(['bar_manager', 'bar_owner', 'super_admin']);
  // const canManageBilling = () => hasRole(['bar_owner']);

  // Initialize theme
  useTheme(theme, setTheme);

  // Initialize all handlers
  const kegHandlers = createKegHandlers(state);
  const beerHandlers = createBeerHandlers(state);
  const locationHandlers = createLocationHandlers(state);
  const accountHandlers = createAccountHandlers(state);
  const maintenanceHandlers = createMaintenanceHandlers(state);

  // Handle deep links from QR codes
  useEffect(() => {
    if (initialKegId && kegs.length > 0 && !isLoading) {
      const keg = kegs.find(k => k.id === initialKegId);
      if (keg) {
        setSelectedKegId(initialKegId);
        setCurrentView('keg');
        // Clean up URL without reloading page
        window.history.replaceState({}, '', '/');
      } else {
        console.warn('Keg ID from QR code not found:', initialKegId);
      }
    }
  }, [initialKegId, kegs, isLoading, setSelectedKegId, setCurrentView]);

  // Additional handler needed for location changes on keg landing page
  const handleKegLocationChange = async (e) => {
    const value = e.target.value;
    if (value === 'ADD_NEW') {
      setCurrentView('settings');
      return;
    }
    const [newLocation, newSublocation] = value.split('|');
    const selectedKeg = kegs.find(k => k.id === selectedKegId);
    if (selectedKeg && newLocation && (newLocation !== selectedKeg.location || newSublocation !== selectedKeg.sublocation)) {
      await SupabaseService.addLocationHistory(selectedKeg.id, newLocation, newSublocation);
      const result = await SupabaseService.updateKeg(selectedKeg.id, {
        location: newLocation,
        sublocation: newSublocation
      });
      if (result.success) {
        const kegsResult = await SupabaseService.fetchKegs();
        if (kegsResult.success !== false) {
          setKegs(kegsResult);
        }
      }
    }
  };

  const handleOpenMaintenanceModal = (keg) => {
    setSelectedMaintenanceKeg(keg);
    setShowMaintenanceModal(true);
  };

  // Computed values
  const selectedKeg = kegs.find(k => k.id === selectedKegId);

  // Use filterKegs utility for cleaner, centralized filter logic
  const filteredKegs = useMemo(() => {
    return filterKegs(kegs, {
      searchTerm,
      filterTapStatus,
      filterCleanStatus,
      filterLocation,
      quickFilter
    });
  }, [kegs, searchTerm, filterTapStatus, filterCleanStatus, filterLocation, quickFilter]);

  // Create location combinations using utility function
  const allLocationCombos = useMemo(() => {
    return createLocationCombinations(locations);
  }, [locations]);


  // Use filterBeersOnTap utility for cleaner logic
  const beersByLocation = useMemo(() => {
    return filterBeersOnTap(allLocationCombos, kegs);
  }, [allLocationCombos, kegs]);

  // Use filterBeersOnDeck utility for cleaner logic
  const onDeckBeersByLocation = useMemo(() => {
    return filterBeersOnDeck(allLocationCombos, kegs);
  }, [allLocationCombos, kegs]);

  // Loading state - wait for both data and auth check
  if (isLoading || !isAuthChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading KegOps...</p>
          <p className="text-gray-400 text-sm mt-2">Connecting to database...</p>
        </div>
      </div>
    );
  }

  // Auth gate: show login if no authenticated user
  if (!account?.session?.user) {
    return <Login />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
            <p className="text-red-600 font-semibold mb-2">Error Loading Data</p>
            <p className="text-red-700 text-sm mb-4">{error}</p>
            <p className="text-gray-600 text-xs">Make sure Supabase is configured correctly and your environment variables are set.</p>
          </div>
          <button 
            onClick={loadAllData}
            className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // Ensure account data is loaded (allow empty name)
  if (!account.profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading account data...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // VIEW ROUTING
  // ============================================

  // Show onboarding if user has no breweries
  if (showOnboarding) {
    return (
      <>
        <OnboardingModal 
          show={true}
          isLoading={isCreatingFirstBrewery}
          onComplete={completeOnboarding}
        />
      </>
    );
  }

  // Tap List View (full-screen, deep-linked) - CHECK THIS FIRST
  if (currentView === 'tap-list' || tapLocation) {
    // Filter beers by location and optional sublocation
    const beers = kegs.filter(k => {
      const kegLoc = normalizeString(k.location);
      const targetLoc = normalizeString(tapLocation);
      const kegSub = normalizeString(k.sublocation);
      const targetSub = normalizeString(tapSublocation);
      const status = normalizeString(k.tapStatus);
      
      const locationMatches = kegLoc === targetLoc;
      const sublocationMatches = !tapSublocation || kegSub === targetSub;
      
      return locationMatches && sublocationMatches && !k.isEmpty && k.currentBeer && status === 'on tap';
    });

    // Build display name
    const displayName = tapSublocation 
      ? `${tapLocation} - ${tapSublocation}` 
      : tapLocation;

    return (
      <>
        <TapListView
          locationName={displayName}
          beers={beers}
          locations={locations}
          account={account}
          selectedBrewery={breweries?.find(b => b.id === selectedBreweryId)}
          onOpenRatingModal={(keg) => { setSelectedRatingKeg(keg); setShowRatingModal(true); }}
          onBackToDashboard={() => { window.location.href = '/'; }}
        />
        <RatingModal
          show={showRatingModal}
          onClose={() => { setShowRatingModal(false); setSelectedRatingKeg(null); setRatingForm({ rating: 0, notes: '', raterName: '' }); }}
          beer={selectedRatingKeg?.currentBeer}
          ratingForm={ratingForm}
          setRatingForm={setRatingForm}
          onSubmit={beerHandlers.handleSubmitRating}
        />
      </>
    );
  }

  // Dashboard View
  if (currentView === 'dashboard') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">

        <div className="flex-1 overflow-auto">
          <DashboardView
          account={account}
          breweries={breweries}
          selectedBreweryId={selectedBreweryId}
          setSelectedBreweryId={setSelectedBreweryId}
          dashboardTab={dashboardTab}
          setDashboardTab={setDashboardTab}
          kegs={kegs}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterTapStatus={filterTapStatus}
          setFilterTapStatus={setFilterTapStatus}
          filterCleanStatus={filterCleanStatus}
          setFilterCleanStatus={setFilterCleanStatus}
          filterLocation={filterLocation}
          setFilterLocation={setFilterLocation}
          showTapDropdown={showTapDropdown}
          setShowTapDropdown={setShowTapDropdown}
          showCleanDropdown={showCleanDropdown}
          setShowCleanDropdown={setShowCleanDropdown}
          toggleTapStatusFilter={(status) => {
            setFilterTapStatus(prev => 
              prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
            );
          }}
          toggleCleanStatusFilter={(status) => {
            setFilterCleanStatus(prev => 
              prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
            );
          }}
          quickFilter={quickFilter}
          onQuickFilterChange={setQuickFilter}
          locations={locations}
          filteredKegs={filteredKegs}
          beersByLocation={beersByLocation}
          onDeckBeersByLocation={onDeckBeersByLocation}
          expandedLocations={expandedLocations}
          toggleLocationExpansion={(locationId) => {
            setExpandedLocations(prev => ({
              ...prev,
              [locationId]: !prev[locationId]
            }));
          }}
          settings={settings}
          calculateAverageRating={calculateAverageRating}
          getTotalRatings={getTotalRatings}
          renderStars={renderStars}
          onAddKeg={() => kegHandlers.handleOpenKegModal()}
          onViewKeg={(kegId) => { setSelectedKegId(kegId); setCurrentView('keg'); }}
          onOpenQRModal={(keg) => { setSelectedKegId(keg.id); setShowQRModal(true); }}
          onEditKeg={(keg) => kegHandlers.handleOpenKegModal(keg)}
          onDeleteKeg={kegHandlers.handleDeleteKeg}
          onOpenRatingModal={(keg) => { setSelectedRatingKeg(keg); setShowRatingModal(true); }}
          onViewHistory={(beer) => { setSelectedHistoryBeer(beer); setShowHistoryModal(true); }}
          onOpenMaintenanceModal={handleOpenMaintenanceModal}
          onSelectKeg={(kegId) => { setSelectedKegId(kegId); setCurrentView('keg'); }}
          onBulkUpdate={kegHandlers.handleBulkUpdate}
          setCurrentView={setCurrentView}
        />
        {/* Dashboard Modals */}
        <KegModal
          show={showKegModal}
          onClose={() => { setShowKegModal(false); setEditingKeg(null); }}
          editingKeg={editingKeg}
          kegForm={kegForm}
          setKegForm={setKegForm}
          settings={settings}
          locations={locations}
          onSave={() => editingKeg ? kegHandlers.handleSaveKeg(editingKeg.id) : kegHandlers.handleAddKeg()}
          onOpenMaintenance={handleOpenMaintenanceModal}
        />
        <QRModal
          show={showQRModal}
          onClose={() => setShowQRModal(false)}
          keg={selectedKeg}
          settings={settings}
          qrSettings={qrSettings}
          setQrSettings={setQrSettings}
        />
        <MaintenanceModal
          show={showMaintenanceModal}
          onClose={() => { setShowMaintenanceModal(false); setSelectedMaintenanceKeg(null); }}
          keg={selectedMaintenanceKeg}
          maintenanceForm={maintenanceForm}
          setMaintenanceForm={setMaintenanceForm}
          onSave={maintenanceHandlers.handleSaveMaintenance}
        />
        <TapNumberModal
          show={showTapNumberModal}
          onClose={() => setShowTapNumberModal(false)}
          defaultValue={kegs.find(k => k.id === tapModalKegId)?.tapNumber || ''}
          onSubmit={(value) => kegHandlers.handleSetTapNumber(value)}
        />
        <RatingModal
          show={showRatingModal}
          onClose={() => { setShowRatingModal(false); setSelectedRatingKeg(null); setRatingForm({ rating: 0, notes: '', raterName: '' }); }}
          beer={selectedRatingKeg?.currentBeer}
          ratingForm={ratingForm}
          setRatingForm={setRatingForm}
          onSubmit={beerHandlers.handleSubmitRating}
        />
        <BeerHistoryModal
          show={showHistoryModal}
          onClose={() => { setShowHistoryModal(false); setSelectedHistoryBeer(null); }}
          beer={selectedHistoryBeer}
          calculateAverageRating={calculateAverageRating}
          getTotalRatings={getTotalRatings}
          renderStars={renderStars}
        />
        </div>
      </div>
    );
  }

  // Keg Landing View
  if (currentView === 'keg' && selectedKeg) {
    return (
      <>
        <KegLandingView
          keg={selectedKeg}
          account={account}
          selectedBrewery={breweries?.find(b => b.id === selectedBreweryId)}
          settings={{ ...settings, locations }}
          onTapStatusChange={(newStatus) => kegHandlers.handleTapStatusChange(selectedKeg.id, newStatus)}
          onCleaningStatusChange={(newStatus) => kegHandlers.handleCleaningStatusChange(selectedKeg.id, newStatus)}
          onLocationChange={handleKegLocationChange}
          onEditTapAssignment={() => {
            state.setTapSelectionKeg(selectedKeg);
            state.setSelectedTapLocation(selectedKeg.location || '');
            state.setSelectedTapSublocation(selectedKeg.sublocation || '');
            state.setSelectedTapName(selectedKeg.tapNumber || '');
            state.setShowTapSelectionModal(true);
          }}
          onMarkEmpty={() => setShowFeedbackModal(true)}
          onRateBeer={() => { setSelectedRatingKeg(selectedKeg); setShowRatingModal(true); }}
          onCleanAndSanitize={() => setShowCleanModal(true)}
          onFillKeg={beerHandlers.handleOpenFillModal}
          onEditBeer={beerHandlers.handleOpenEditBeer}
          onViewHistory={(beer) => { setSelectedHistoryBeer(beer); setShowHistoryModal(true); }}
          onOpenQRModal={(keg) => setShowQRModal(true)}
          setCurrentView={setCurrentView}
        />
        {/* Keg-specific Modals */}
        <TapSelectionModal
          show={state.showTapSelectionModal}
          onClose={() => state.setShowTapSelectionModal(false)}
          keg={state.tapSelectionKeg}
          locations={locations}
          kegs={kegs}
          onSave={kegHandlers.handleSaveTapSelection}
          onMoveBeer={kegHandlers.handleMoveBeerToTap}
          selectedLocation={state.selectedTapLocation}
          setSelectedLocation={state.setSelectedTapLocation}
          selectedSublocation={state.selectedTapSublocation}
          setSelectedSublocation={state.setSelectedTapSublocation}
          selectedTap={state.selectedTapName}
          setSelectedTap={state.setSelectedTapName}
        />
        <FeedbackModal
          show={showFeedbackModal}
          onClose={() => { setShowFeedbackModal(false); state.setFeedbackRating(0); state.setFeedbackNotes(""); state.setFeedbackName(""); }}
          beer={selectedKeg?.currentBeer}
          feedbackRating={state.feedbackRating}
          setFeedbackRating={state.setFeedbackRating}
          feedbackNotes={state.feedbackNotes}
          setFeedbackNotes={state.setFeedbackNotes}
          feedbackName={state.feedbackName}
          setFeedbackName={state.setFeedbackName}
          onSubmit={beerHandlers.handleSubmitFeedback}
        />
        <CleanModal
          show={showCleanModal}
          onClose={() => setShowCleanModal(false)}
          onClean={async (status) => {
            await kegHandlers.handleCleaningStatusChange(selectedKeg.id, status);
            setShowCleanModal(false);
          }}
        />
        <FillKegModal
          show={showFillModal}
          onClose={() => { 
            setShowFillModal(false); 
            state.setStyleSearch(""); 
            state.setShowStyleDropdown(false); 
            state.setBeerImageFile(null); 
            state.setBeerImagePreview(null);
            state.setNewBeer({ name: "", style: "", abv: "", ibu: "", og: "", fg: "", description: "", imageUrl: null, brewery: "", location: "" });
          }}
          newBeer={state.newBeer}
          setNewBeer={state.setNewBeer}
          styleSearch={state.styleSearch}
          setStyleSearch={state.setStyleSearch}
          showStyleDropdown={state.showStyleDropdown}
          setShowStyleDropdown={state.setShowStyleDropdown}
          beerImagePreview={state.beerImagePreview}
          onImageUpload={beerHandlers.handleImageUpload}
          onFill={state.isEditingBeer ? beerHandlers.handleUpdateCurrentBeer : beerHandlers.handleFillKeg}
          isEditing={state.isEditingBeer}
          locations={locations}
          selectedKeg={selectedKeg}
        />
        <TapNumberModal
          show={showTapNumberModal}
          onClose={() => setShowTapNumberModal(false)}
          defaultValue={kegs.find(k => k.id === tapModalKegId)?.tapNumber || ''}
          onSubmit={(value) => kegHandlers.handleSetTapNumber(value)}
        />
        <BeerHistoryModal
          show={showHistoryModal}
          onClose={() => { setShowHistoryModal(false); setSelectedHistoryBeer(null); }}
          beer={selectedHistoryBeer}
          calculateAverageRating={calculateAverageRating}
          getTotalRatings={getTotalRatings}
          renderStars={renderStars}
        />
        <QRModal
          show={showQRModal}
          onClose={() => setShowQRModal(false)}
          keg={selectedKeg}
          settings={settings}
          qrSettings={qrSettings}
          setQrSettings={setQrSettings}
        />
        <RatingModal
          show={showRatingModal}
          onClose={() => { setShowRatingModal(false); setSelectedRatingKeg(null); setRatingForm({ rating: 0, notes: '', raterName: '' }); }}
          beer={selectedKeg?.currentBeer}
          ratingForm={ratingForm}
          setRatingForm={setRatingForm}
          onSubmit={beerHandlers.handleSubmitRating}
        />
      </>
    );
  }

  // Account View
  if (currentView === 'account') {
    return (
      <>
        <AccountView
          account={account}
          setAccount={(newAccount) => accountHandlers.handleUpdateAccount(newAccount)}
          breweries={breweries}
          setBreweries={setBreweries}
          selectedBreweryId={selectedBreweryId}
          setSelectedBreweryId={setSelectedBreweryId}
          onLogoUpload={accountHandlers.handleLogoUpload}
          setCurrentView={setCurrentView}
        />
        <UserModal
          show={showUserModal}
          onClose={() => { setShowUserModal(false); state.setEditingUser(null); }}
          editingUser={state.editingUser}
          userForm={userForm}
          setUserForm={setUserForm}
          onSave={accountHandlers.handleSaveUser}
        />
      </>
    );
  }

  // Settings View
  if (currentView === 'settings') {
    // Check role can manage settings
    if (!canManageSettings()) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">You don't have permission to access settings.</p>
              <button
                onClick={() => setCurrentView('dashboard')}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <div className="flex-1 overflow-auto">
          <SettingsView
          settings={settings}
          account={account}
          setSettings={maintenanceHandlers.handleUpdateSettings}
          theme={theme}
          setTheme={setTheme}
          locations={locations}
          onAddLocation={() => { setEditingLocation(null); setNewLocation({ name: '', description: '' }); setShowLocationModal(true); }}
          onEditLocation={locationHandlers.handleEditLocation}
          onAddSublocation={locationHandlers.handleAddOrUpdateSublocation}
          onEditSublocation={locationHandlers.handleEditSublocation}
          onDeleteSublocation={locationHandlers.handleDeleteSublocation}
          onDeleteLocation={locationHandlers.handleDeleteLocation}
          setCurrentView={setCurrentView}
          selectedBreweryId={selectedBreweryId}
          breweries={breweries}
          setBreweries={setBreweries}
          userRole={selectedBreweryId && breweries ? (breweries.find(b => b.id === selectedBreweryId)?.userRole || null) : null}
        />
        <LocationModal
          show={showLocationModal}
          onClose={() => { 
            setShowLocationModal(false); 
            setEditingLocation(null);
            setNewLocation({ name: '', description: '' }); 
          }}
          editingLocation={editingLocation}
          newLocation={newLocation}
          setNewLocation={setNewLocation}
          onAdd={locationHandlers.handleAddOrUpdateLocation}
          selectedBreweryId={selectedBreweryId}
        />
        <SublocationModal
          show={showSublocationModal}
          onClose={() => { 
            setShowSublocationModal(false); 
            setEditingSublocation(null);
            setNewSublocation({ name: '', description: '', parentId: null }); 
          }}
          editingSublocation={editingSublocation}
          newSublocation={newSublocation}
          setNewSublocation={setNewSublocation}
          locations={locations}
          onAdd={() => locationHandlers.handleAddOrUpdateSublocation()}
          selectedBreweryId={selectedBreweryId}
        />
        </div>
      </div>
    );
  }

  return null;
}
